import { styled } from "@mui/material/styles";
import { Box, Typography, TextField, CircularProgress } from "@mui/material";
import { motion } from "framer-motion";
import { useEffect } from "react";
import { format } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../stores";
import { getBillingDetailsByUserId } from "../../features/subscription-module/subscriptionAction";

const Card = styled(Box)(({ theme }) => ({
  backgroundColor: "#f8f8f8",
  borderRadius: "8px",
  padding: theme.spacing(2),
  width: "100%",
  maxWidth: "auto",
  minHeight: 380,
  margin: theme.spacing(2, "auto"),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5),
    maxWidth: "100%",
    margin: theme.spacing(1, 0),
    minHeight: 320,
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  fontSize: "20px",
  fontWeight: 600,
  color: "#4b5b5d",
  textAlign: "left",
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    fontSize: "18px",
  },
}));

const DetailRow = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: theme.spacing(1),
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "flex-start",
    marginBottom: theme.spacing(1),
    gap: theme.spacing(0.5),
  },
}));

const DetailLabel = styled(Typography)(({ theme }) => ({
  color: "#757575",
  fontSize: "11px",
  fontWeight: 500,
  [theme.breakpoints.down("sm")]: {
    fontSize: "11px",
    lineHeight: 1.4,
  },
}));

const DetailValue = styled(TextField)(({ theme }) => ({
  "& .MuiInputBase-input": {
    color: "#4b5b5d",
    fontSize: "11px",
    fontWeight: 500,
    textAlign: "right",
    padding: theme.spacing(1.1),
    [theme.breakpoints.down("sm")]: {
      fontSize: "11px",
      textAlign: "left",
      padding: theme.spacing(0.75),
    },
  },
  "& .MuiInputBase-root": {
    backgroundColor: "#fff",
    borderRadius: 4,
    "& fieldset": {
      border: "1px solid #afb6b6",
    },
  },
  width: "60%",
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
}));

const NoDetailsMessage = styled(Typography)(({ theme }) => ({
  color: "#757575",
  fontSize: "14px",
  fontWeight: 500,
  textAlign: "center",
  marginTop: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    fontSize: "12px",
  },
}));

const ErrorMessage = styled(Typography)(({ theme }) => ({
  color: "#d32f2f",
  fontSize: "14px",
  fontWeight: 500,
  textAlign: "center",
  marginTop: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    fontSize: "12px",
  },
}));

// Loader Container
const LoaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "360px",
  width: "100%",
  height: "100%",
}));

const LoaderText = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
  fontSize: "0.9rem",
  color: "#4b5b5d",
  fontWeight: 500,
}));

const BillingDetailsCard = () => {
  const dispatch = useDispatch();
  const { billingDetails, billingDetailLoading, billingDetailError } =
    useSelector((state: RootState) => state.subscription);

  useEffect(() => {
    dispatch(getBillingDetailsByUserId() as any);
  }, [dispatch]);

  const details =
    billingDetails && billingDetails.length > 0 ? billingDetails[0] : null;

  const isNoBilling =
    billingDetailError &&
    billingDetailError.includes("Billing details not found");

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      <Card>
        <Title>Billing Details</Title>
        <Box sx={{ flexGrow: 1 }}>
          {billingDetailLoading ? (
            <LoaderContainer>
              <CircularProgress
                size={40}
                thickness={4}
                sx={{
                  color: "#4b5b5d",
                }}
              />
              <LoaderText>Loading billing details...</LoaderText>
            </LoaderContainer>
          ) : billingDetailError ? (
            isNoBilling ? (
              <NoDetailsMessage>
                No billing details available. Your subscription may have been
                cancelled.
              </NoDetailsMessage>
            ) : (
              <ErrorMessage>{billingDetailError}</ErrorMessage>
            )
          ) : details ? (
            <>
              <DetailRow>
                <DetailLabel>Plan</DetailLabel>
                <DetailValue
                  value={details.planName}
                  InputProps={{ readOnly: true }}
                />
              </DetailRow>
              <DetailRow>
                <DetailLabel>Payment Method</DetailLabel>
                <DetailValue
                  value={
                    details.paymentMethod.charAt(0).toUpperCase() +
                    details.paymentMethod.slice(1)
                  }
                  InputProps={{ readOnly: true }}
                />
              </DetailRow>
              <DetailRow>
                <DetailLabel>Card Number</DetailLabel>
                <DetailValue
                  value={details.cardNumber || "N/A"}
                  InputProps={{ readOnly: true }}
                />
              </DetailRow>
              <DetailRow>
                <DetailLabel>Billing Email</DetailLabel>
                <DetailValue
                  value={details.billingEmail}
                  InputProps={{ readOnly: true }}
                />
              </DetailRow>
              <DetailRow>
                <DetailLabel>Renewal Date</DetailLabel>
                <DetailValue
                  value={format(new Date(details.renewalDate), "MM-dd-yyyy")}
                  InputProps={{ readOnly: true }}
                />
              </DetailRow>
              <DetailRow>
                <DetailLabel>Amount</DetailLabel>
                <DetailValue
                  value={`$${details.amount.toLocaleString()}`}
                  InputProps={{ readOnly: true }}
                />
              </DetailRow>
            </>
          ) : (
            <NoDetailsMessage>
              No billing details available. Your subscription may have been
              cancelled.
            </NoDetailsMessage>
          )}
        </Box>
      </Card>
    </motion.div>
  );
};

export default BillingDetailsCard;
